import React, { useState, useMemo } from 'react';
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  // CircularProgress,
} from '@mui/material';
// import { SelectOption } from '../../components/MasterComponent/Select/MultipleSelect';
import Loading from '../../components/Loading/Loading';

interface ComparisonTableProps {
  data: any[];
  selectedAccounts: string[];
  // accountOptions: SelectOption[];
  currency?: string;
  isLoading?: boolean;
}

const ComparisonTable: React.FC<ComparisonTableProps> = ({
  data,
  selectedAccounts,
  // accountOptions,
  currency = 'USD',
  isLoading = false,
}) => {
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Get region label from options
  // const getAccountLabel = (accountId: string) => {
  //   const option = accountOptions.find((opt) => opt.value === accountId);
  //   return option ? option.label : `Account ${accountId}`;
  // };

  // Use all data from API (no client-side filtering)
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data;
  }, [data]);

  // Handle sorting
  const handleSort = (column: string) => {
    const newDirection =
      sortColumn === column && sortDirection === 'desc' ? 'asc' : 'desc';
    setSortColumn(column);
    setSortDirection(newDirection);
  };

  // Sort the data based on current sort settings
  const sortedData = useMemo(() => {
    if (!sortColumn || !filteredData.length) return filteredData;

    return [...filteredData].sort((a, b) => {
      let valueA: number;
      let valueB: number;

      if (sortColumn === 'service') {
        // Sort by service name alphabetically
        const nameA = a.service.toLowerCase();
        const nameB = b.service.toLowerCase();
        if (sortDirection === 'asc') {
          return nameA.localeCompare(nameB);
        } else {
          return nameB.localeCompare(nameA);
        }
      } else {
        // Sort by account value
        valueA = parseFloat(a[sortColumn] || '0');
        valueB = parseFloat(b[sortColumn] || '0');
        return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
      }
    });
  }, [filteredData, sortColumn, sortDirection]);

  // Format currency values
  const formatCurrency = (value: string | number): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '-';

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(numValue);
  };

  // Render sort indicator
  const renderSortIndicator = (column: string) => {
    if (column !== sortColumn) {
      return <span style={{ color: 'gray' }}> ▼</span>;
    }
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  // Get cell styling for negative values
  const getCellStyle = (value: string | number) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (numValue < 0) {
      return {
        // color: '#dc2626',
        fontWeight: 600,
      };
    }
    return {};
  };

  // No data state
  // if (!filteredData.length || !selectedAccounts.length) {
  //   return (
  //     <Paper className="table-container" style={{ marginTop: '2rem' }}>
  //       <div style={{ padding: '2rem', textAlign: 'center' }}>
  //         {selectedAccounts.length === 0
  //           ? 'No data available'
  //           : 'No data available for the selected filters'}
  //       </div>
  //     </Paper>
  //   );
  // }

  return (
    <div style={{ marginTop: '2rem' }}>
      <Paper className="table-container">
        <Table
          className="table adminTable adminPanelTable"
          size="small"
          aria-label="comparison table"
          stickyHeader
        >
          <TableHead className="admintableHead">
            <TableRow>
              <TableCell
                className="sticky-cell adminTableheading"
                style={{
                  zIndex: 8,
                  minWidth: '21rem',
                  cursor: 'pointer',
                }}
                onClick={() => handleSort('service')}
              >
                Service
                {renderSortIndicator('service')}
              </TableCell>
              {selectedAccounts.map((accountId) => (
                <TableCell
                  key={accountId}
                  className="adminTableheading"
                  align="center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort(accountId)}
                >
                  {accountId}
                  {renderSortIndicator(accountId)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={selectedAccounts.length + 1} align="center">
                  <Loading style={{ padding: '2rem' }} />
                </TableCell>
              </TableRow>
            ) : filteredData.length ? (
              sortedData.map((item, index) => (
                <TableRow key={`${item.service}-${index}`}>
                  <TableCell
                    className="sticky-cell"
                    component="th"
                    scope="row"
                    style={{
                      fontWeight: '600',
                      whiteSpace: 'break-spaces',
                      zIndex: 5,
                    }}
                  >
                    {item.service}
                  </TableCell>
                  {selectedAccounts.map((accountId) => {
                    const value = item[accountId];
                    return (
                      <TableCell
                        key={`${accountId}-${index}`}
                        align="center"
                        style={getCellStyle(value)}
                      >
                        {value !== undefined && value !== null
                          ? formatCurrency(value)
                          : '-'}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={selectedAccounts.length + 1} align="center">
                  No Records
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </div>
  );
};

export default ComparisonTable;
